/**
 * The current route, and how to change it.
 *
 * `pushState` plus a `popstate` listener. `pushState` does not fire `popstate`
 * — that is the API's design, not an oversight — so `navigate` sets the state
 * itself; a listener alone would leave the sidebar dead to its own clicks.
 */

import { useCallback, useEffect, useState } from "react";
import { resolveRoute, routePath, type RouteId } from "../routes";

export function useRoute(): { route: RouteId; navigate: (next: RouteId) => void } {
  const [route, setRoute] = useState<RouteId>(() => resolveRoute(window.location.pathname));

  useEffect(() => {
    const onPop = (): void => setRoute(resolveRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((next: RouteId) => {
    // Keep the query string. The dev shell fixture lives in it, and losing it on
    // the first click would make the pane-swap test navigate out of the fixture.
    window.history.pushState(null, "", `${routePath(next)}${window.location.search}`);
    setRoute(next);
  }, []);

  return { route, navigate };
}
